import { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase } from '../../../../services/workQueue';
import { approveCase } from '../../../../services/approval';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import type { OrthopulseLevel } from './orthopulseLevel';
import { ConfirmModalShell } from './ConfirmModalShell';
import { ConfirmModalHeader } from './ConfirmModalHeader';
import { ConfirmModalBody } from './ConfirmModalBody';
import { ConfirmModalFooter } from './ConfirmModalFooter';
import { PatientSummaryCard } from './PatientSummaryCard';
import { AlignerOptionList, type AlignerOption } from './AlignerOptionList';
import { OrthopulseToggleRow } from './OrthopulseToggleRow';
import { reportApprovalActionFailure } from './reportApprovalActionFailure';

const APPROVE_ACTION = 'approve-case';

interface ApproveCaseModalProps {
    workCase: WorkQueueCase;
    /** Resolved via resolveOrthopulseLevel: Dx report status, else Monday's legacy recommendation. */
    orthopulseLevel: OrthopulseLevel | null;
    onClose: () => void;
    onSuccess: (actionId: string) => void;
}

function useOrthopulseToggle(orthopulseLevel: OrthopulseLevel | null) {
    const [addOrthopulse, setAddOrthopulse] = useState(false);
    // Tracks a manual toggle so a late-arriving orthopulseLevel (Dx report /
    // Monday fallback still loading) can't clobber the admin's own choice.
    const userTouchedRef = useRef(false);
    const isMandatory = orthopulseLevel === 'mandatory';
    const isRecommended = orthopulseLevel === 'recommended';

    useEffect(() => {
        if (isMandatory) {
            setAddOrthopulse(true);
            return;
        }
        if (userTouchedRef.current) return;
        setAddOrthopulse(isRecommended);
    }, [isMandatory, isRecommended]);

    const toggle = (): void => {
        if (isMandatory) return;
        userTouchedRef.current = true;
        setAddOrthopulse((prev) => !prev);
    };

    return { addOrthopulse, isMandatory, isRecommended, toggle };
}

function useApproveSubmit(c: WorkQueueCase, onSuccess: (actionId: string) => void) {
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);

    const submit = async (option: AlignerOption, addOrthopulse: boolean): Promise<void> => {
        setSubmitting(true);
        try {
            await approveCase(c.id, { option, addOrthopulse });
            showToast(`Case approved: ${c.name}`, 'success');
            onSuccess(APPROVE_ACTION);
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to approve case. Please try again.');
            showToast(message, 'error');
            reportApprovalActionFailure(
                'ApproveCaseModal.handleConfirm',
                APPROVE_ACTION,
                c.id,
                message,
                err,
                {
                    option,
                    addOrthopulse
                }
            );
            setSubmitting(false);
        }
    };

    return { submitting, submit };
}

export function ApproveCaseModal({
    workCase: c,
    orthopulseLevel,
    onClose,
    onSuccess
}: ApproveCaseModalProps) {
    const [option, setOption] = useState<AlignerOption | null>(null);
    const { addOrthopulse, isMandatory, isRecommended, toggle } =
        useOrthopulseToggle(orthopulseLevel);
    const { submitting, submit } = useApproveSubmit(c, onSuccess);

    const canSubmit = option !== null && !submitting;
    const handleConfirm = (): void => {
        if (!option || !canSubmit) return;
        void submit(option, addOrthopulse);
    };

    return (
        <ConfirmModalShell widthClassName="w-[480px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={CheckCircleIcon}
                iconClassName="w-5 h-5 text-green-600"
                title="Approve case"
                onClose={onClose}
                disabled={submitting}
            />

            <ConfirmModalBody className="space-y-4">
                <PatientSummaryCard name={c.name} practice={c.practice} />
                <AlignerOptionList selected={option} onSelect={setOption} disabled={submitting} />
                <OrthopulseToggleRow
                    on={addOrthopulse}
                    isMandatory={isMandatory}
                    isRecommended={isRecommended}
                    disabled={submitting}
                    onToggle={toggle}
                />
            </ConfirmModalBody>

            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={handleConfirm}
                cancelDisabled={submitting}
                confirmDisabled={!canSubmit}
                confirmLabel={submitting ? 'Approving…' : 'Confirm approve'}
                confirmColorClassName="bg-gray-800 text-white hover:bg-gray-900"
            />
        </ConfirmModalShell>
    );
}
